import { getEnvironmentConfig, getBaseApiUrl, getAgentId } from "../utils/config";
import { OptionalDatasource, EmailEnabledAgent, KarliUser } from "../types";
import { extractUserDraft, splitEmailBody } from "../utils/emailUtils";
import { buildPrompt, UseCase } from "../types/useCase";

export interface GenerateEmailParams {
  emailContent: string;
  assistant: string;
  accessToken: string;
  agentId?: string;
  userContext?: string;
  lastHighlightedText?: string;
}

interface KarliApiRequest {
  model: string;
  messages: Array<{ role: string; content: string }>;
  stream: boolean;
  source: string;
  metadata?: {
    [key: string]: unknown;
  };
}

interface KarliApiResponse {
  chatId: string;
  assistantMessageId: string;
  choices: Array<{
    message: {
      content: string;
    };
  }>;
}

export async function generateEmailResponse(
  params: GenerateEmailParams & { selectedCase: UseCase }
): Promise<KarliApiResponse> {
  const { emailContent, assistant, accessToken, agentId, userContext, selectedCase } = params;

  console.log("1. generateEmailResponse called", { selectedCase, agentId, userContext });
  const { primary, context } = splitEmailBody(emailContent);
  console.log("2. Building prompt for", selectedCase);
  const config = getEnvironmentConfig();
  const modelId = agentId || config.model;
  let userDraft: string | undefined;
  let primaryEmail: string | undefined;

  switch (selectedCase) {
    case "Umschreiben":
    case "Grammatik checken":
    case "Professionalisieren":
      userDraft = extractUserDraft(emailContent);
      primaryEmail = undefined;
      break;
    case "Eigene Anweisungen":
    default:
      primaryEmail = primary;
      userDraft = extractUserDraft(emailContent) || undefined;
      break;
  }
  const prompt = buildPrompt({
    selectedCase,
    primary: primaryEmail ?? "",
    userDraft: userDraft ?? "",
    context,
    userContext,
  });
  console.log("3. Prompt built, sending request to", config.apiUrl);
  const messages = [
    {
      role: "user",
      content: prompt,
    },
  ];

  const requestBody: KarliApiRequest = {
    model: modelId,
    messages,
    stream: false,
    source: "OUTLOOK",
    metadata: {
      Optional: assistant,
    },
  };

  const response = await fetch(config.apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `API-Anfrage fehlgeschlagen: ${response.status} ${response.statusText} - ${errorText}`
    );
  }
  console.log("4. Response received", response.status, response.statusText);

  const data: KarliApiResponse = await response.json();

  if (!data.choices || data.choices.length === 0) {
    throw new Error("Keine Antwort von der API erhalten");
  }

  return data;
}

export async function fetchOptionalDatasources(accessToken: string): Promise<OptionalDatasource[]> {
  const baseUrl = getBaseApiUrl();
  const agentId = getAgentId();

  const response = await fetch(`${baseUrl}/api/v1/agents/${agentId}/optionalDatasources`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Datenquellen konnten nicht abgerufen werden: ${response.status} ${response.statusText} - ${errorText}`
    );
  }

  return response.json();
}

export async function fetchEmailEnabledAgents(accessToken: string): Promise<EmailEnabledAgent[]> {
  const baseUrl = getBaseApiUrl();

  const response = await fetch(`${baseUrl}/api/v1/oa/outlook/models?emailEnabled=true`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `E-Mail-fähige Agents konnten nicht abgerufen werden: ${response.status} ${response.statusText} - ${errorText}`
    );
  }

  const json = await response.json();

  const agents: EmailEnabledAgent[] = json.data.map((agent: any) => ({
    id: agent.id,
    name: agent.name ?? agent.subtext ?? agent.id,
    subtext: agent.subtext ?? "",
    isEmailEnabled: agent.isEmailEnabled ?? false,
  }));

  return agents;
}

export async function getCurrentUser(accessToken: string): Promise<KarliUser> {
  const baseUrl = getBaseApiUrl();
  const response = await fetch(`${baseUrl}/api/v1/me?lang=de`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user: ${response.statusText}`);
  }

  return response.json();
}

export async function getConversationSummary(
  conversationId: string,
  mailboxOwner: string,
  sumUpModelId: string,

  accessToken: string
): Promise<string> {
  const baseUrl = getBaseApiUrl();

  const response = await fetch(`${baseUrl}/api/v1/oa/outlook/summarizeConversation`, {
    method: "POST",

    headers: {
      Authorization: `Bearer ${accessToken}`,

      "Content-Type": "application/json",
    },

    body: JSON.stringify({
      conversationId,
      mailboxOwner,
      sumUpModelId,
    }),
  });

  if (!response.ok) {
    throw new Error("Backend request failed");
  }

  const data = await response.json();

  return data.summary;
}
